import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Palette, Check, X, Clock, CheckCircle, XCircle, Star, Upload, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending: { label: "Pending", icon: Clock, color: "text-yellow-400", border: "border-yellow-400/40" },
  approved: { label: "Approved", icon: CheckCircle, color: "text-[#39FF14]", border: "border-[#39FF14]/40" },
  denied: { label: "Denied", icon: XCircle, color: "text-red-400", border: "border-red-400/40" },
};

export default function AdminDesignRequestsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [decideModal, setDecideModal] = useState(null); // {req, action}
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showcaseModal, setShowcaseModal] = useState(null); // req
  const [showcaseCaption, setShowcaseCaption] = useState("");
  const [showcaseImages, setShowcaseImages] = useState([]);
  const [showcaseFeatured, setShowcaseFeatured] = useState(false);
  const [showcaseUploading, setShowcaseUploading] = useState(false);
  const showcaseFileRef = useRef(null);

  const fetchReqs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/design-requests`);
      setReqs(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    fetchReqs();
  }, [user, authLoading, navigate, fetchReqs]);

  const openDecide = (req, action) => { setDecideModal({ req, action }); setNote(""); setError(""); };

  const confirm = async () => {
    if (!decideModal) return;
    const status = decideModal.action;
    if (status === "denied" && !note.trim()) { setError("Please explain why in a short note."); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/design-requests/${decideModal.req.id}`, { status, admin_note: note });
      setDecideModal(null);
      setNote("");
      await fetchReqs();
    } catch (e) { setError(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const openShowcase = (req) => {
    setShowcaseModal(req);
    setShowcaseCaption(req.completion_caption || "");
    setShowcaseImages(req.completion_image_urls || []);
    setShowcaseFeatured(!!req.featured);
    setError("");
  };

  const handleShowcaseUpload = async (files) => {
    if (!files || files.length === 0) return;
    if (showcaseImages.length + files.length > 6) { setError("Max 6 photos."); return; }
    setShowcaseUploading(true);
    setError("");
    const newUrls = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/api/upload/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        newUrls.push(`${API}/api/files/${data.path}`);
      } catch { setError("An image failed to upload."); }
    }
    setShowcaseImages([...showcaseImages, ...newUrls]);
    setShowcaseUploading(false);
    if (showcaseFileRef.current) showcaseFileRef.current.value = "";
  };

  const removeShowcaseImage = (url) => setShowcaseImages(showcaseImages.filter(u => u !== url));

  const saveShowcase = async () => {
    if (!showcaseModal) return;
    setSaving(true);
    setError("");
    try {
      await axios.put(`${API}/api/admin/design-requests/${showcaseModal.id}/showcase`, {
        completion_caption: showcaseCaption,
        completion_image_urls: showcaseImages,
        featured: showcaseFeatured,
      });
      setShowcaseModal(null);
      await fetchReqs();
    } catch (e) { setError(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}><div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" /></div>;

  const filtered = reqs.filter(r => statusFilter === "all" || r.status === statusFilter);
  const counts = {
    all: reqs.length,
    pending: reqs.filter(r => r.status === "pending").length,
    approved: reqs.filter(r => r.status === "approved").length,
    denied: reqs.filter(r => r.status === "denied").length,
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <button onClick={() => navigate("/admin")} className="flex items-center text-[#A1A1A1] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider mb-3" data-testid="back-to-admin">
          <ArrowLeft className="h-3 w-3 mr-2" /> Back to Admin
        </button>
        <div className="flex items-center gap-2 mb-2">
          <Palette className="h-5 w-5 text-[#39FF14]" strokeWidth={1.5} />
          <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14]">Admin</p>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2] mb-8" style={{ fontFamily: "Chivo, sans-serif" }}>
          Design Requests
        </h1>

        <div className="flex gap-[1px] mb-6" data-testid="design-status-tabs">
          {["pending", "approved", "denied", "all"].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] font-bold transition-colors ${statusFilter === s ? "bg-[#39FF14] text-[#050505]" : "bg-[#0D0D0D] text-[#A1A1A1] hover:text-[#39FF14] border border-[#1A1A1A]"}`}
            >
              {s} ({counts[s]})
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="bg-[#0D0D0D] border border-[#1A1A1A] p-16 text-center">
            <p className="text-[#A1A1A1] font-mono text-sm">No {statusFilter} requests.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="design-list">
            {filtered.map(r => {
              const s = STATUS_META[r.status];
              const Icon = s.icon;
              return (
                <div key={r.id} className={`bg-[#0D0D0D] border ${s.border} p-5`} data-testid={`design-req-${r.id}`}>
                  <div className="flex justify-between items-start gap-3 mb-3">
                    <div>
                      <p className="font-mono text-[10px] text-[#A1A1A1] uppercase tracking-wider">#{r.id.slice(-8)} · {new Date(r.created_at).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
                      <p className="text-[#F2F2F2] font-bold mt-1">{r.user_name || "(no name)"}</p>
                      <p className="font-mono text-[10px] text-[#A1A1A1]">{r.user_email}</p>
                    </div>
                    <div className={`flex items-center gap-1 px-3 py-1 ${s.color} font-mono text-[10px] uppercase tracking-wider bg-[#050505] border ${s.border}`}>
                      <Icon className="h-3 w-3" />{s.label}
                    </div>
                  </div>
                  <p className="text-[#F2F2F2] text-sm whitespace-pre-wrap mb-3 leading-relaxed">{r.description}</p>
                  {r.image_urls?.length > 0 && (
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-3" data-testid={`design-images-${r.id}`}>
                      {r.image_urls.map((url, idx) => (
                        <a key={idx} href={url} target="_blank" rel="noreferrer" className="aspect-square block border border-[#1A1A1A] hover:border-[#39FF14] overflow-hidden">
                          <img src={url} alt="ref" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                  {r.admin_note && (
                    <div className="p-3 bg-[#050505] border border-[#1A1A1A] mb-3">
                      <p className={`font-mono text-[10px] uppercase tracking-wider ${s.color} mb-1`}>Your Note</p>
                      <p className="text-[#F2F2F2] text-xs whitespace-pre-wrap">{r.admin_note}</p>
                    </div>
                  )}
                  {r.status === "pending" && (
                    <div className="flex gap-2">
                      <Button onClick={() => openDecide(r, "approved")} data-testid={`approve-${r.id}`} className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono text-xs uppercase tracking-wider">
                        <Check className="h-3 w-3 mr-1" /> Approve
                      </Button>
                      <Button onClick={() => openDecide(r, "denied")} data-testid={`deny-${r.id}`} variant="outline" className="bg-transparent border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-none font-mono text-xs uppercase tracking-wider">
                        <X className="h-3 w-3 mr-1" /> Deny
                      </Button>
                    </div>
                  )}
                  {r.status === "approved" && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button onClick={() => openShowcase(r)} data-testid={`showcase-${r.id}`} className="bg-[#0D0D0D] border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10 rounded-none font-mono text-xs uppercase tracking-wider">
                        <Star className="h-3 w-3 mr-1" /> {r.featured ? "Edit Homepage Feature" : "Feature On Homepage"}
                      </Button>
                      {r.featured && (
                        <span className="font-mono text-[10px] uppercase tracking-wider text-[#39FF14] flex items-center gap-1" data-testid={`featured-badge-${r.id}`}>
                          <Star className="h-3 w-3 fill-current" /> Featured
                        </span>
                      )}
                      {r.completion_image_urls?.length > 0 && (
                        <span className="font-mono text-[10px] uppercase tracking-wider text-[#A1A1A1]">
                          {r.completion_image_urls.length} build photo{r.completion_image_urls.length !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {decideModal && (
        <div className="fixed inset-0 z-50 bg-[#050505]/90 flex items-center justify-center p-4" data-testid="decide-modal">
          <div className="bg-[#0D0D0D] border border-[#1A1A1A] max-w-lg w-full p-6">
            <h3 className="text-lg font-black uppercase tracking-tighter text-[#F2F2F2] mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>
              {decideModal.action === "approved" ? "Approve Design" : "Deny Design"}
            </h3>
            <p className="text-[#A1A1A1] text-xs font-mono mb-4">For {decideModal.req.user_email} — #{decideModal.req.id.slice(-8)}</p>
            <label className="block font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-2">
              {decideModal.action === "approved" ? "Optional note to customer" : "Reason (shown to customer)"}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={5}
              maxLength={2000}
              placeholder={decideModal.action === "approved" ? "e.g. Great taste — we'll email you shortly to finalize pricing." : "e.g. We can't source translucent purple shells at this time."}
              className="w-full bg-[#050505] border border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:outline-none focus:ring-1 focus:ring-[#39FF14] px-3 py-2 text-sm font-mono resize-none"
              data-testid="decide-note"
            />
            {error && <p className="text-red-400 text-xs font-mono mt-2">{error}</p>}
            <div className="flex gap-2 justify-end mt-4">
              <Button onClick={() => setDecideModal(null)} variant="outline" className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] rounded-none font-mono text-xs uppercase tracking-wider">Cancel</Button>
              <Button onClick={confirm} disabled={saving} data-testid="decide-confirm" className={`${decideModal.action === "approved" ? "bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E]" : "bg-red-500 text-white hover:bg-red-600"} rounded-none font-mono text-xs uppercase tracking-wider disabled:opacity-50`}>
                {saving ? "Saving…" : decideModal.action === "approved" ? "Confirm Approve" : "Confirm Deny"}
              </Button>
            </div>
          </div>
        </div>
      )}
      {showcaseModal && (
        <div className="fixed inset-0 z-50 bg-[#050505]/90 flex items-center justify-center p-4 overflow-y-auto" data-testid="showcase-modal">
          <div className="bg-[#0D0D0D] border border-[#1A1A1A] max-w-lg w-full p-6 my-8">
            <div className="flex items-center gap-2 mb-1">
              <Star className="h-4 w-4 text-[#39FF14]" />
              <h3 className="text-lg font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
                Homepage Feature
              </h3>
            </div>
            <p className="text-[#A1A1A1] text-xs font-mono mb-4">
              For {showcaseModal.user_email} — #{showcaseModal.id.slice(-8)}. Upload photos of the finished build to show off on the homepage Design Studio.
            </p>

            <label className="block font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-2">Short Caption (optional)</label>
            <input
              type="text"
              value={showcaseCaption}
              onChange={(e) => setShowcaseCaption(e.target.value)}
              maxLength={200}
              placeholder="e.g. Translucent purple w/ chrome vents"
              className="w-full bg-[#050505] border border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:outline-none focus:ring-1 focus:ring-[#39FF14] px-3 py-2 text-sm font-mono mb-4"
              data-testid="showcase-caption"
            />

            <label className="block font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-2">Finished Build Photos ({showcaseImages.length}/6)</label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {showcaseImages.map((url, idx) => (
                <div key={idx} className="relative aspect-square border border-[#1A1A1A] bg-[#050505] group" data-testid={`showcase-photo-${idx}`}>
                  <img src={url} alt="build" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeShowcaseImage(url)} className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity" data-testid={`showcase-remove-${idx}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {showcaseImages.length < 6 && (
                <button
                  type="button"
                  onClick={() => showcaseFileRef.current?.click()}
                  disabled={showcaseUploading}
                  className="aspect-square border border-dashed border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] flex flex-col items-center justify-center transition-colors disabled:opacity-50"
                  data-testid="showcase-upload-btn"
                >
                  <Upload className="h-5 w-5 mb-1" />
                  <span className="font-mono text-[9px] uppercase tracking-wider">{showcaseUploading ? "…" : "Add"}</span>
                </button>
              )}
            </div>
            <input ref={showcaseFileRef} type="file" accept="image/*" multiple onChange={(e) => handleShowcaseUpload(Array.from(e.target.files))} className="hidden" data-testid="showcase-file-input" />

            <label className="flex items-center gap-2 cursor-pointer mb-4 select-none" data-testid="showcase-featured-toggle">
              <input
                type="checkbox"
                checked={showcaseFeatured}
                onChange={(e) => setShowcaseFeatured(e.target.checked)}
                className="w-4 h-4 accent-[#39FF14]"
              />
              <span className="font-mono uppercase tracking-[0.15em] text-xs text-[#F2F2F2]">Feature on homepage</span>
            </label>

            {error && <p className="text-red-400 text-xs font-mono mb-3" data-testid="showcase-error">{error}</p>}

            <div className="flex gap-2 justify-end">
              <Button onClick={() => setShowcaseModal(null)} variant="outline" className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] rounded-none font-mono text-xs uppercase tracking-wider">Cancel</Button>
              <Button onClick={saveShowcase} disabled={saving || showcaseUploading} data-testid="showcase-save" className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono text-xs uppercase tracking-wider disabled:opacity-50">
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
